import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Divider,
    Typography,
    useMediaQuery,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
    useGetIndividualReservationQuery,
    useGetJobsQuery,
} from '../../../../../app/Slices/SevenDayApiSlice';
import { CustomChip } from '../../../../../layout/styledcomponents/CustomChip';
import { theme } from '../../../../../shared/theme';
import { Status } from '../../../../enums/contractor';
import BookingCharges from '../../../Bookings/Fees/BookingCharges';
import AddOnsCharges from '../../../Bookings/Fees/AddOnsCharges';
import { BookingSetMenu } from '../../../../types/set-menu';
import { BookingSetMenuStatus } from '../../../../enums/set-menu';
import { Job, ProviderType } from '../../../../../app/Slices/SevenDayApiTypes';
import { sum } from '../../../../../shared/utils';
import ReservationJobsCharges from '../../../Bookings/Fees/ReservationJobsCharges';

type Props = {
    reservation: any;
    asset: any;
    expanded: boolean;
    onToggle: (event: React.SyntheticEvent, isExpanded: boolean) => void;
    jumpToServices?: boolean;
};

export default function ReservationChargesSection({
    reservation,
    asset,
    expanded,
    onToggle,
    jumpToServices,
}: Props) {
    const isMobile = useMediaQuery('(max-width: 770px)');

    const [expandedServiceJobs, setExpandedServiceJobs] = useState(false);
    const [expandedAddOns, setExpandedAddOns] = useState(false);

    const serviceJobsChargesRef = useRef<HTMLDivElement | null>(null);
    const addOnsChargesRef = useRef<HTMLDivElement | null>(null);

    const hasCharges = (reservation?.charges?.length || 0) > 0;
    const hasGuestPayments =
        (reservation?.guestPaymentHistory?.length || 0) > 0;

    const { data: jobs } = useGetJobsQuery(
        { bookingId: reservation?.id },
        { skip: !reservation?.id },
    );

    const setMenuJobs: BookingSetMenu[] = reservation.setMenus ?? [];

    const allServiceJobs = [
        ...(jobs || []).map((job) => ({
            ...job,
            providerType: ProviderType.Contractor,
        })),
        ...setMenuJobs.map((setMenu) => ({
            ...setMenu,
            providerType: ProviderType.Owner,
        })),
    ] as (Job | BookingSetMenu)[];

    const addOns = reservation.linkedBookings
        .filter(({ isAddOn }: { isAddOn: boolean }) => isAddOn)
        .map(({ id }: { id: number }) => ({
            id,
            ...useGetIndividualReservationQuery(`/bookings/${id}`),
        }));

    const hasServiceJobs = allServiceJobs.length > 0;
    const hasAddOns = addOns.length > 0;

    const serviceJobCount = allServiceJobs.length;

    const totalContractorJobsAmount = useMemo(() => {
        const filteredJobs = (jobs || []).filter(
            (job) => ![Status.Cancelled, Status.Declined].includes(job.status),
        );
        const items = filteredJobs.flatMap((job) => job?.items ?? []);

        return items.reduce(
            (sum: number, item) => sum + (Number(item?.total) || 0),
            0,
        );
    }, [jobs]);

    const totalSetMenuAmount = sum(
        setMenuJobs
            .filter(
                (setMenuJob) =>
                    setMenuJob.status !== BookingSetMenuStatus.Cancelled,
            )
            .map((setMenu) => setMenu.total),
    );

    const totalServiceJobsAmount =
        totalContractorJobsAmount + totalSetMenuAmount;

    const totalAddOnsAmount = useMemo(() => {
        return addOns.reduce(
            (sum: number, addOn: any) =>
                sum + (addOn.data?.bookingTotal.total ?? 0),
            0,
        );
    }, [addOns]);

    const totalServiceJobsAmountFormatted = useMemo(() => {
        return new Intl.NumberFormat('en-AU', {
            style: 'currency',
            currency: 'AUD',
        }).format(totalServiceJobsAmount);
    }, [totalServiceJobsAmount]);

    const totalFormatted = useMemo(() => {
        const bookingTotal = reservation?.bookingTotal?.total || 0;

        const expensesTotal =
            reservation?.expenses?.reduce(
                (sum: number, expense: any) => sum + (expense?.amount || 0),
                0,
            ) || 0;

        const combinedTotal = bookingTotal + expensesTotal;

        return new Intl.NumberFormat('en-AU', {
            style: 'currency',
            currency: 'AUD',
        }).format(combinedTotal);
    }, [reservation?.bookingTotal?.total, reservation?.expenses]);

    const isPaid =
        (reservation?.guestPaymentHistory?.length || 0) > 0 &&
        reservation.guestPaymentHistory[0].fullyPaidAt;
    const hasTotal = (reservation?.bookingTotal?.total || 0) !== 0;

    const outerAccordionSx = {
        width: '100%',
        py: 1.25,
        boxShadow: 'none',
        backgroundColor: theme.palette.custom.cardActionBackground,
        transition: 'background-color 120ms ease',
        '&.Mui-expanded': {
            backgroundColor: theme.palette.neutral.main,
        },
    };

    const summarySx = {
        py: 1.25,
        px: isMobile ? 2 : 2,
    };

    const detailsSx = {
        px: isMobile ? 2 : 3,
        pb: isMobile ? 2 : 3,
        pt: 0,
        backgroundColor: theme.palette.neutral.main,
    };

    const cardSx = {
        borderRadius: 2,
        boxShadow: 'none',
        px: isMobile ? 1.5 : 2,
        py: 1.5,
    };

    const innerAccordionSx = {
        borderRadius: 2,
        boxShadow: 'none',
        backgroundColor: theme.palette.custom.cardActionBackground,
        transition: 'background-color 120ms ease',
        '&.Mui-expanded': {
            backgroundColor: theme.palette.neutral.main,
        },
    };

    useEffect(() => {
        if (!jumpToServices) return;

        if (hasServiceJobs) {
            setExpandedServiceJobs(true);
            setTimeout(() => {
                serviceJobsChargesRef.current?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                });
            }, 80);
        }
    }, [jumpToServices, hasServiceJobs]);

    const empty = !hasCharges && !hasGuestPayments;

    const hasAdditionalExpenses =
        (reservation?.expenses?.reduce(
            (sum: number, expense: any) => sum + (expense?.amount || 0),
            0,
        ) || 0) > 0;

    return (
        <>
            <Accordion
                expanded={expanded}
                onChange={onToggle}
                disableGutters
                square={false}
                sx={outerAccordionSx}
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls='charges-content'
                    id='charges-header'
                    sx={summarySx}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            width: '100%',
                            gap: 2,
                            flexWrap: 'wrap',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                gap: 0.25,
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.25,
                                    flexWrap: 'wrap',
                                }}
                            >
                                <Typography
                                    variant='h2'
                                    sx={{
                                        letterSpacing: 0.2,
                                        color: 'text.secondary',
                                    }}
                                >
                                    Total Charges
                                </Typography>
                                {!empty && hasTotal ? (
                                    <CustomChip
                                        size='small'
                                        variant='outlined'
                                        label={
                                            isPaid ? 'Paid' : 'Pending Payment'
                                        }
                                        color={isPaid ? 'green' : 'red'}
                                        sx={{
                                            fontSize: 12,
                                            whiteSpace: 'nowrap',
                                        }}
                                    />
                                ) : null}
                            </Box>

                            {hasAdditionalExpenses ? (
                                <Typography
                                    variant='body3'
                                    sx={{
                                        color: 'text.secondary',
                                        fontStyle: 'italic',
                                    }}
                                >
                                    (inc additional expenses)
                                </Typography>
                            ) : null}
                        </Box>

                        {!empty ? (
                            <Typography
                                variant='body2'
                                sx={{ fontWeight: 'bold' }}
                            >
                                {totalFormatted}
                            </Typography>
                        ) : null}
                    </Box>
                </AccordionSummary>

                <AccordionDetails sx={{ ...detailsSx, px: 0 }}>
                    <Divider sx={{ mt: 0, mb: 2, px: 0 }} />

                    {empty ? (
                        <Alert
                            severity='info'
                            sx={{
                                ...cardSx,
                                mb: 0,
                                fontSize: 13,
                                fontWeight: 800,
                                py: 2,
                                justifyContent: 'center',
                                textAlign: 'center',
                                '& .MuiAlert-icon': { display: 'none' },
                                '& .MuiAlert-message': {
                                    width: '100%',
                                    textAlign: 'center',
                                },
                            }}
                        >
                            No charges have been made
                        </Alert>
                    ) : (
                        <Box sx={{ mt: 2 }}>
                            <BookingCharges
                                charges={reservation?.charges}
                                invoice={reservation?.invoice}
                                bookingTotal={reservation?.bookingTotal}
                                expenses={reservation?.expenses}
                                module={asset}
                                refunds={reservation?.refund}
                                reservation={reservation}
                                billingExcluded={reservation?.billingExcluded}
                                manualDiscount={!!reservation?.manualDiscount}
                            />
                        </Box>
                    )}
                </AccordionDetails>
            </Accordion>

            {hasServiceJobs ? (
                <Box ref={serviceJobsChargesRef} sx={{ mt: 2 }}>
                    <Accordion
                        expanded={expandedServiceJobs}
                        onChange={() => setExpandedServiceJobs((v) => !v)}
                        disableGutters
                        square={false}
                        sx={innerAccordionSx}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls='serviceJobsCharges-content'
                            id='serviceJobsCharges-header'
                            sx={summarySx}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    width: '100%',
                                    gap: 2,
                                    flexWrap: 'wrap',
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                    }}
                                >
                                    <Typography
                                        variant='h2'
                                        sx={{
                                            letterSpacing: 0.2,
                                            color: 'text.secondary',
                                        }}
                                    >
                                        Estimated Service Charges
                                    </Typography>
                                    <Typography
                                        variant='body3'
                                        sx={{ color: 'text.secondary' }}
                                    >
                                        {serviceJobCount}{' '}
                                        {serviceJobCount === 1 ? 'job' : 'jobs'}
                                    </Typography>
                                </Box>

                                <Typography
                                    variant='body2'
                                    sx={{ fontWeight: 'bold' }}
                                >
                                    {totalServiceJobsAmountFormatted}
                                </Typography>
                            </Box>
                        </AccordionSummary>

                        <AccordionDetails sx={detailsSx}>
                            <Divider sx={{ mt: 0, mb: 2 }} />
                            <Box sx={cardSx}>
                                <ReservationJobsCharges jobs={allServiceJobs} />
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                </Box>
            ) : null}

            {hasAddOns && (
                <Box ref={addOnsChargesRef} sx={{ mt: 2 }}>
                    <Accordion
                        expanded={expandedAddOns}
                        onChange={() => setExpandedAddOns((v) => !v)}
                        disableGutters
                        square={false}
                        sx={innerAccordionSx}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls='addOns-content'
                            id='addOns-header'
                            sx={summarySx}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    width: '100%',
                                    gap: 2,
                                    flexWrap: 'wrap',
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                    }}
                                >
                                    <Typography
                                        variant='h2'
                                        sx={{
                                            letterSpacing: 0.2,
                                            color: 'text.secondary',
                                        }}
                                    >
                                        Add-ons Charges
                                    </Typography>
                                    <Typography
                                        variant='body3'
                                        sx={{
                                            color: theme.palette.text.secondary,
                                        }}
                                    >
                                        {addOns.length}{' '}
                                        {addOns.length === 1
                                            ? 'add-on'
                                            : 'add-ons'}
                                    </Typography>
                                </Box>
                                <Typography
                                    variant='body2'
                                    sx={{ fontWeight: 'bold' }}
                                >
                                    {new Intl.NumberFormat('en-AU', {
                                        style: 'currency',
                                        currency: 'AUD',
                                    }).format(totalAddOnsAmount)}
                                </Typography>
                            </Box>
                        </AccordionSummary>

                        <AccordionDetails sx={detailsSx}>
                            <Divider sx={{ mt: 0, mb: 2 }} />
                            <Box sx={cardSx}>
                                <AddOnsCharges addOns={addOns} />
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                </Box>
            )}
        </>
    );
}
